import type { FC } from 'react';

import { ConditionalWrapper } from '../conditional-wrapper';
import { Flex, Avatar, Tooltip, Typography, type FlexProps } from 'antd';
import SvgUser from '@shared/assets/icons/components/User';
import styles from './user.module.scss';

export interface UserProps extends Omit<FlexProps, 'children'> {
  size?: 'sm' | 'md';
  avatar?: string | null;
  fullName?: string;
  fullNameId?: string;
  caption?: string;
  captionId?: string;
  maxNameLenght?: number;
  isTooltipVisible?: boolean;
  avatarBackgroundColor?: string;
}

const MAX_CAPTION_LENGTH = 25;
const MAX_NAME_LENGTH = 35;

export const User: FC<UserProps> = ({
  size = 'sm',
  avatar,
  fullName,
  fullNameId,
  caption,
  captionId,
  maxNameLenght = MAX_NAME_LENGTH,
  isTooltipVisible = true,
  avatarBackgroundColor,
  ...flexProps
}) => {
  const { Text } = Typography;

  const hasLongName = fullName && fullName?.length > maxNameLenght;
  const nameSlice = hasLongName ? `${fullName.slice(0, maxNameLenght)}...` : fullName;

  const hasLongCaption = caption && caption?.length > MAX_CAPTION_LENGTH;
  const captionSlice = hasLongCaption ? `${caption.slice(0, MAX_CAPTION_LENGTH)}...` : caption;
  const initials = fullName
    ?.trim()
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toLocaleUpperCase();
  const shouldUseInitials = Boolean(avatarBackgroundColor);

  return (
    <Flex {...flexProps} align="center" className={styles.root} gap="8px">
      <Flex className={styles.avatar_container} align="center" justify="center">
        <Avatar
          className={styles.avatar_container}
          src={avatar}
          icon={shouldUseInitials ? undefined : <SvgUser />}
          style={
            avatarBackgroundColor
              ? { backgroundColor: avatarBackgroundColor, color: '#FFFFFF', fontWeight: 500 }
              : undefined
          }
        >
          {shouldUseInitials ? initials : undefined}
        </Avatar>
      </Flex>
      <Flex className={styles.container} vertical gap="2px">
        {fullName && (
          <Text className={styles.title} data-testid={fullNameId}>
            <ConditionalWrapper
              condition={isTooltipVisible && (hasLongName || false)}
              wrapper={(children) => (
                <Tooltip title={fullName} placement="top" className={styles.tooltip}>
                  {children}
                </Tooltip>
              )}
            >
              <>{nameSlice}</>
            </ConditionalWrapper>
          </Text>
        )}

        {caption && (
          <Text className={styles.caption} data-testid={captionId}>
            <ConditionalWrapper
              condition={isTooltipVisible && (hasLongCaption || false)}
              wrapper={(children) => (
                <Tooltip title={caption} placement="top" className={styles.tooltip}>
                  {children}
                </Tooltip>
              )}
            >
              <>{captionSlice}</>
            </ConditionalWrapper>
          </Text>
        )}
      </Flex>
    </Flex>
  );
};
